import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CopyableContainer from "./CopyableContainer";
import PendingDataNotice from "./PendingDataNotice";
import LeagueTabHeading from "./LeagueTabHeading";
import LeagueTabSkeleton from "./LeagueTabSkeleton";
import SimulationPlayoffSummary, { getSimulationRowClassName } from "./SimulationPlayoffSummary";
import PlayoffTableImageCopyButton from "./PlayoffTableImageCopyButton";
import { rankDistributionImageOptions } from "../utils/playoffTableImage";
import { probabilityHeatStyle } from "../utils/simulationHeatmap";
import './styles/tableStyles.css';

const RankDistributionTable = ({
    data,
    numColumns,
    playoffTeams,
    selectedWeek,
    pendingData,
}) => {
    const [sortConfig, setSortConfig] = useState(null);
    const sortedRows = useMemo(() => {
        if (!Array.isArray(data)) return [];

        const rows = data.map((team, originalIndex) => ({ team, originalIndex }));
        if (!sortConfig) return rows;

        const direction = sortConfig.direction === "ascending" ? 1 : -1;
        return rows.sort((left, right) => {
            const leftValue = Number(
                sortConfig.key === "playoff_odds"
                    ? left.team.playoff_odds
                    : left.team.position_odds?.[sortConfig.key]
            );
            const rightValue = Number(
                sortConfig.key === "playoff_odds"
                    ? right.team.playoff_odds
                    : right.team.position_odds?.[sortConfig.key]
            );
            const numericDifference =
                (Number.isFinite(leftValue) ? leftValue : 0) -
                (Number.isFinite(rightValue) ? rightValue : 0);

            return numericDifference === 0
                ? left.originalIndex - right.originalIndex
                : numericDifference * direction;
        });
    }, [data, sortConfig]);

    if (data === null || numColumns === null || playoffTeams === null) {
        return <LeagueTabSkeleton variant="rank-distribution" label="Calculating rank distribution" />;
    }

    const ordinalSuffix = (num) => {
        const suffixes = ["th", "st", "nd", "rd"];
        const value = num % 100;
        return (
            num +
            (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0])
        );
    };

    function formatPercent(val) {
        const percentage = val * 100;
        return `${percentage.toFixed(val < 0.1 ? 1 : 0)}%`;
    }

    const handleSort = (key) => {
        setSortConfig((current) => ({
            key,
            direction:
                current?.key === key && current.direction === "descending"
                    ? "ascending"
                    : "descending",
        }));
    };

    const sortableHeader = (label, key) => {
        const isActive = sortConfig?.key === key;
        const direction = isActive ? sortConfig.direction : null;
        const nextDirection = direction === "descending" ? "ascending" : "descending";

        return (
            <th aria-sort={direction || undefined}>
                <button
                    type="button"
                    className={`simulation-sort-button ${isActive ? "simulation-sort-button--active" : ""}`}
                    aria-label={`Sort by ${label} ${nextDirection}`}
                    onClick={() => handleSort(key)}
                >
                    <span>{label}</span>
                    <span className="simulation-sort-indicator" aria-hidden="true">
                        {direction === "ascending" ? "\u2191" : direction === "descending" ? "\u2193" : "\u2195"}
                    </span>
                </button>
            </th>
        );
    };

    return (
        <CopyableContainer
            title={
                <div className="rank-distribution-heading">
                    <LeagueTabHeading
                        title="Final Position Distribution"
                    />
                    <PlayoffTableImageCopyButton
                        imageOptions={rankDistributionImageOptions({
                            data,
                            numColumns,
                            playoffTeams,
                            selectedWeek,
                        })}
                        fileName={`rank-distribution-week-${selectedWeek}`}
                        disabled={!Array.isArray(data)}
                    />
                </div>
            }
            fileName={`rank-distribution-week-${selectedWeek}`}
            className="simulation-copyable-shell rank-distribution-copyable"
        >
            <PendingDataNotice
                dataType="final position distributions"
                isPending={pendingData}
            />
            <div className="table-rounded-shell">
            <table className="table-with-bottom-caption">
                <thead>
                    <tr>
                        <th className="team-name-column">Team</th>
                        <th className="team-name-column">Owner</th>
                        {Array.from({ length: numColumns }, (_, i) => (
                            <React.Fragment key={i}>
                                {sortableHeader(`${ordinalSuffix(i + 1)} place`, i)}
                            </React.Fragment>
                        ))}
                        {sortableHeader("Playoff Odds", "playoff_odds")}
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map(({ team, originalIndex }, displayIndex) => {
                        const outcomeClassName = getSimulationRowClassName(
                            originalIndex,
                            playoffTeams,
                            false
                        )
                            .split(" ")
                            .filter((className) => className.startsWith("simulation-row-"))
                            .join(" ");

                        return (
                        <tr
                            key={originalIndex}
                            className={`${displayIndex % 2 === 0 ? "even-row" : "odd-row"}${outcomeClassName ? ` ${outcomeClassName}` : ""}`}
                        >
                            <td className="team-name-column">
                                {team.team}
                            </td>
                            <td className="team-name-column">
                                {team.owner}
                            </td>
                            {team.position_odds
                                .slice(0, numColumns)
                                .map((odds, i) => (
                                    <td key={i} className="simulation-heat-cell">
                                        <div className="simulation-heat" style={probabilityHeatStyle(odds)}>
                                            {formatPercent(odds)}
                                        </div>
                                    </td>
                                ))}
                            <td className="simulation-heat-cell">
                                <div
                                    className="simulation-heat"
                                    style={probabilityHeatStyle(team.playoff_odds)}
                                >
                                    {formatPercent(team.playoff_odds)}
                                </div>
                            </td>
                        </tr>
                        );
                    })}
                </tbody>
            </table>
            </div>
            <SimulationPlayoffSummary playoffTeams={playoffTeams} showByeChip={false} />
            <p className="simulation-table-disclaimer">
                    All percentages are based on simulation results alone.
                    Values of 0% or 100% do not necessarily mean that a team has
                    been mathematically eliminated or clinched a playoff spot.
            </p>
        </CopyableContainer>
    );
};

RankDistributionTable.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            team: PropTypes.string.isRequired,
            owner: PropTypes.string.isRequired,
            position_odds: PropTypes.arrayOf(PropTypes.string).isRequired,
            playoff_odds: PropTypes.string.isRequired,
        })
    ),
    numColumns: PropTypes.number.isRequired,
    playoffTeams: PropTypes.number.isRequired,
};

export default RankDistributionTable;
